import CupDisplay from "../../components/track-components/CupDisplay";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLoaderData, useSearchParams } from "react-router-dom";
import { Checkbox, FormControlLabel, Autocomplete, TextField, Pagination, CircularProgress } from "@mui/material";
import { useFirebase } from "../../../contexts/FirebaseContext";
import { fetchCupsCount, loadCupsPage } from "../../functions/browseFilters";
import { getAllTracks } from "../../functions/trackFilters";
import "../../../App.css";
import "../../../shared/pages/Browse/SharedBrowse.css";
import "./Browse.css";

export default function Browse() {

    const [cups, setCups] = useState([]);
    const data = useLoaderData();
    const { firestore } = useFirebase();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get all available tracks for the autocomplete
    const allTracks = useMemo(() => getAllTracks(), []);

    // Initialize filter states from URL params
    const getInitialTracks = () => {
        const tracksParam = searchParams.get('tracks');
        if (!tracksParam) return [];
        const trackNames = tracksParam.split(',');
        return allTracks.filter(track => trackNames.includes(track.name));
    };

    const getInitialExcludeDLC = () => {
        return searchParams.get('excludeDLC') === 'true';
    };

    const getInitialPage = () => {
        const page = parseInt(searchParams.get('page'));
        return page > 0 ? page : 1;
    };

    const getInitialPageSize = () => {
        const size = parseInt(searchParams.get('pageSize'));
        return [12, 24, 48, 96].includes(size) ? size : 24;
    };

    const getInitialSort = () => {
        const sort = searchParams.get('sort');
        return sort === 'oldest' ? 'oldest' : 'newest';
    };

    // Filter states
    const [selectedTracks, setSelectedTracks] = useState(getInitialTracks());
    const [excludeDLC, setExcludeDLC] = useState(getInitialExcludeDLC());
    const [sortOrder, setSortOrder] = useState(getInitialSort());
    const [currentPage, setCurrentPage] = useState(getInitialPage());
    const [loading, setLoading] = useState(false);
    const [cupsPerPage, setCupsPerPage] = useState(getInitialPageSize());

    // Track total count and pagination
    const [totalCount, setTotalCount] = useState(data?.length || 0);
    const [filteredCount, setFilteredCount] = useState(0);
    const [clientFiltered, setClientFiltered] = useState(false);
    const [lastVisible, setLastVisible] = useState(null);

    // Update URL params when filters change
    const updateURLParams = useCallback((tracks, dlc, sort, page, pageSize) => {
        const params = {};
        
        if (tracks.length > 0) {
            params.tracks = tracks.map(t => t.name).join(',');
        }
        
        if (dlc) {
            params.excludeDLC = 'true';
        }
        
        if (sort !== 'newest') {
            params.sort = sort;
        }
        
        if (page > 1) {
            params.page = page.toString();
        }
        
        if (pageSize !== 24) {
            params.pageSize = pageSize.toString();
        }
        
        setSearchParams(params, { replace: true });
    }, [setSearchParams]);

    // Fetch total count based on current filters
    const fetchTotalCount = useCallback(async () => {
        try {
            const count = await fetchCupsCount(firestore, { excludeDLC, selectedTracks, sortOrder });
            setTotalCount(count);
        } catch (error) {
            console.error('Error fetching count:', error);
        }
    }, [firestore, excludeDLC, selectedTracks, sortOrder]);

    // Load cups for current page
    const loadCups = useCallback(async (pageNum = 1, resetLastVisible = false) => {
        setLoading(true);
        
        try {
            const currentLastVisible = resetLastVisible ? null : lastVisible;
            const result = await loadCupsPage(
                firestore,
                { excludeDLC, selectedTracks, sortOrder },
                { cupsPerPage, lastVisible: currentLastVisible, pageNum }
            );
            
            setCups(result.cups);
            setLastVisible(result.lastVisible);
            setClientFiltered(result.clientFiltered);
            setFilteredCount(result.filteredCount);
        } catch (error) {
            console.error('Error loading cups:', error);
        } finally {
            setLoading(false);
        }
    }, [firestore, excludeDLC, selectedTracks, sortOrder, lastVisible, cupsPerPage]);

    // Initialize with loader data and fetch actual count
    useEffect(() => {
        setCups(data);
        setTotalCount(data?.length || 0);
        // Fetch the actual total count from Firestore
        fetchTotalCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Reload when filters or page size change
    useEffect(() => {
        setCurrentPage(1);
        setLastVisible(null);
        loadCups(1, true);
        fetchTotalCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTracks, excludeDLC, sortOrder, cupsPerPage]);
    
    // Load new page when page changes
    useEffect(() => {
        if (currentPage > 1) {
            loadCups(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    // Pagination
    const displayCount = clientFiltered ? filteredCount : totalCount;
    const totalPages = Math.ceil(displayCount / cupsPerPage);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        updateURLParams(selectedTracks, excludeDLC, sortOrder, value, cupsPerPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTrackChange = (event, newValue) => {
        if (newValue.length <= 4) {
            setSelectedTracks(newValue);
            updateURLParams(newValue, excludeDLC, sortOrder, 1, cupsPerPage);
        }
    };

    const handleExcludeDLCChange = (e) => {
        const newValue = e.target.checked;
        setExcludeDLC(newValue);
        updateURLParams(selectedTracks, newValue, sortOrder, 1, cupsPerPage);
    };

    const handleSortChange = (event, newValue) => {
        if (newValue) {
            setSortOrder(newValue);
            updateURLParams(selectedTracks, excludeDLC, newValue, 1, cupsPerPage);
        }
    };

    const handlePageSizeChange = (event, newValue) => {
        if (newValue) {
            setCupsPerPage(newValue);
            updateURLParams(selectedTracks, excludeDLC, sortOrder, 1, newValue);
        }
    };

    return <div id="main-container" className="browse">
        <h1>Browse Cups</h1>
        
        <div className="filter-container">
            <Autocomplete
                multiple
                id="track-filter"
                options={allTracks}
                getOptionLabel={(option) => option.name}
                value={selectedTracks}
                onChange={handleTrackChange}
                renderInput={(params) => (
                    <TextField {...params} label="Tracks (all selected)" variant="outlined" />
                )}
                sx={{
                    minWidth: 200,
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                            borderColor: 'white',
                        },
                        '&:hover fieldset': {
                            borderColor: 'white',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'white',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: 'white',
                    },
                    '& .MuiChip-root': {
                        backgroundColor: '#555',
                        color: 'white',
                    },
                }}
            />

            <Autocomplete
                id="sort-order"
                options={['newest', 'oldest']}
                getOptionLabel={(option) => option === 'newest' ? 'Newest First' : 'Oldest First'}
                value={sortOrder}
                onChange={handleSortChange}
                renderInput={(params) => (
                    <TextField {...params} label="Sort By" variant="outlined" />
                )}
                sx={{
                    minWidth: 140,
                    '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                            borderColor: 'white',
                        },
                        '&:hover fieldset': {
                            borderColor: 'white',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'white',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: 'white',
                    },
                }}
            />

            <Autocomplete
                id="page-size"
                options={[12, 24, 48, 96]}
                getOptionLabel={(option) => option.toString()}
                value={cupsPerPage}
                onChange={handlePageSizeChange}
                renderInput={(params) => (
                    <TextField {...params} label="Per Page" variant="outlined" />
                )}
                sx={{
                    minWidth: 120,
                    '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                            borderColor: 'white',
                        },
                        '&:hover fieldset': {
                            borderColor: 'white',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'white',
                        },
                    },
                    '& .MuiInputLabel-root': {
                        color: 'white',
                    },
                }}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={excludeDLC}
                        onChange={handleExcludeDLCChange}
                        sx={{
                            color: 'white',
                            '&.Mui-checked': {
                                color: 'white',
                            },
                        }}
                    />
                }
                label="Exclude DLC Tracks"
                sx={{ color: 'white' }}
            />
        </div>

        <p className="cup-count">
            Showing {cups.length} of {displayCount} cups (Page {currentPage} of {totalPages})
        </p>

        <ol id="cup-list">
            {
                cups.length > 0 ?
                cups.map((element, index) => {
                    return <li key={index} tabIndex="-1" className="trackContainer">
                        <Link to={`/mk8dx/browse/cup/${element.id}`} reloadDocument>
                            <h5 tabIndex={-1}>{element.cupName}</h5>
                            <CupDisplay
                                tracks={element.tracks}
                                variant="browse"
                                showEmblems={true}
                            />
                        </Link>
                    </li>
                })
                : loading ? <CircularProgress sx={{ color: 'white' }} /> : <p>No cups found matching your filters.</p>
            }
        </ol>

        {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                <CircularProgress sx={{ color: 'white' }} />
            </div>
        )}

        {totalPages > 1 && (
            <div className="pagination-container">
                <Pagination 
                    count={totalPages} 
                    page={currentPage} 
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                        '& .MuiPaginationItem-root': {
                            color: 'white',
                        },
                        '& .Mui-selected': {
                            backgroundColor: '#555 !important',
                        },
                    }}
                />
            </div>
        )}
    </div>
}
